// Fetch the signed-in user's M365 profile photo from Microsoft Graph.
// Returns an object URL the consumer can drop into <img src="…">; null
// when the user has no photo set or the fetch fails (consumer falls back
// to initials).
//
// On InteractionRequiredAuthError (Graph consent missing), silently fall
// back to null — the avatar should never bounce the user back to consent.
// Ported from cmms-workorder portal.

import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphScopes } from './msalConfig';
import { signedInAccount } from './ssoMemory';

export function useUserPhoto(): string | null {
  const { instance, accounts } = useMsal();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    // The ACTIVE account, never simply the first cached (auth/ssoMemory.ts).
    const account = signedInAccount(instance, accounts);
    if (!account) {
      setPhotoUrl(null);
      return;
    }
    let alive = true;
    let createdUrl: string | null = null;

    (async () => {
      try {
        let result;
        try {
          result = await instance.acquireTokenSilent({ account, scopes: graphScopes });
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) return;
          throw err;
        }
        const resp = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${result.accessToken}` },
        });
        if (!resp.ok) return;
        const blob = await resp.blob();
        createdUrl = URL.createObjectURL(blob);
        if (alive) setPhotoUrl(createdUrl);
        else URL.revokeObjectURL(createdUrl);
      } catch {
        // tolerate; initials is the safe fallback
      }
    })();

    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [instance, accounts]);

  return photoUrl;
}
